import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Anchor, Plus, Eye, Trash2, LogOut, Search } from "lucide-react";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/certificates");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id) => {
    try {
      await api.delete(`/certificates/${id}`);
      toast.success("Certificate revoked");
      setItems((x) => x.filter((c) => c.id !== id));
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const filtered = items.filter((c) => {
    const s = q.toLowerCase();
    return (
      !s ||
      c.holder_name.toLowerCase().includes(s) ||
      c.coc_number.toLowerCase().includes(s) ||
      c.rank.toLowerCase().includes(s) ||
      c.flag_state.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen parchment" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-[#0a1d3f] text-[#f4ecd5] border-b-4 border-[#d4af37]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Anchor className="h-7 w-7 text-[#d4af37]" />
            <div>
              <div className="font-serif-display tracking-[0.25em] text-sm">
                FLAG·STATE REGISTRY
              </div>
              <div className="text-xs text-[#e6dcc0]/70 font-serif-body">
                Endorsement Issuance Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-serif-body text-sm text-[#e6dcc0]/80 hidden md:block">
              {user?.email}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="bg-transparent border-[#d4af37]/60 text-[#f4ecd5] hover:bg-[#d4af37]/10 hover:text-[#f4ecd5] rounded-none"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="font-serif-body text-sm uppercase tracking-[0.3em] text-[#b8860b]">
              Issued Endorsements
            </p>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#0a1d3f] mt-2">
              Certificate Ledger
            </h1>
            <div className="brass-divider w-32 mt-3" />
          </div>
          <Button
            onClick={() => navigate("/issue")}
            className="bg-[#0a1d3f] hover:bg-[#13295a] text-[#f4ecd5] font-serif-display tracking-widest rounded-none h-11 px-6 border border-[#d4af37]/40"
            data-testid="issue-new-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Issue New Endorsement
          </Button>
        </div>

        <div className="mb-6 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
          <Input
            placeholder="Search by name, COC number, rank, flag…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 bg-white/70 border-stone-400 rounded-none"
            data-testid="search-input"
          />
        </div>

        <div className="bg-white/60 border border-stone-400 rounded-none overflow-hidden shadow-sm">
          <Table data-testid="certificates-table">
            <TableHeader>
              <TableRow className="bg-[#0a1d3f] hover:bg-[#0a1d3f]">
                <TableHead className="text-[#f4ecd5] font-serif-display tracking-wider text-xs">
                  COC №
                </TableHead>
                <TableHead className="text-[#f4ecd5] font-serif-display tracking-wider text-xs">
                  Holder
                </TableHead>
                <TableHead className="text-[#f4ecd5] font-serif-display tracking-wider text-xs">
                  Rank
                </TableHead>
                <TableHead className="text-[#f4ecd5] font-serif-display tracking-wider text-xs">
                  Flag State
                </TableHead>
                <TableHead className="text-[#f4ecd5] font-serif-display tracking-wider text-xs">
                  Issued
                </TableHead>
                <TableHead className="text-[#f4ecd5] font-serif-display tracking-wider text-xs">
                  Valid Until
                </TableHead>
                <TableHead className="text-[#f4ecd5] font-serif-display tracking-wider text-xs text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-stone-600">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16">
                    <div className="font-serif-body text-stone-700">
                      No endorsements issued yet.
                    </div>
                    <Button
                      variant="link"
                      onClick={() => navigate("/issue")}
                      className="text-[#0a1d3f] mt-2"
                      data-testid="empty-issue-link"
                    >
                      Issue your first endorsement →
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow key={c.id} className="hover:bg-[#f4ecd5]/40">
                    <TableCell className="font-mono text-sm text-[#a82828] font-bold">
                      {c.coc_number}
                    </TableCell>
                    <TableCell className="font-serif-body font-semibold text-[#0a1d3f]">
                      {c.holder_name}
                    </TableCell>
                    <TableCell className="font-serif-body">{c.rank}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="border-[#0a1d3f]/40 text-[#0a1d3f] rounded-none font-serif-body"
                      >
                        {c.flag_state}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-serif-body text-sm">
                      {c.issued_on}
                    </TableCell>
                    <TableCell className="font-serif-body text-sm">
                      {c.valid_until}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/certificate/${c.id}`)}
                        className="hover:bg-[#0a1d3f]/10"
                        data-testid={`view-cert-${c.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-red-100 text-red-700"
                            data-testid={`delete-cert-${c.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke endorsement?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete the endorsement
                              record for <strong>{c.holder_name}</strong> (COC{" "}
                              {c.coc_number}). The verification page will no
                              longer resolve.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDelete(c.id)}
                              className="bg-red-700 hover:bg-red-800"
                              data-testid="confirm-delete-btn"
                            >
                              Revoke
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
